/**
 * BIGWA Core - Memory Cache (L1)
 * 
 * Cache em memória com TTL (Time To Live).
 * Usado como primeira camada de cache (L1) para acesso ultra-rápido.
 */

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Entrada do cache com metadados
 */
interface CacheEntry<T> {
  value: T;
  expiresAt: number;
  createdAt: number;
}

/**
 * Configuração do Memory Cache
 */
export interface MemoryCacheConfig {
  /**
   * TTL padrão em ms (padrão: 5 minutos)
   */
  defaultTtl?: number;

  /**
   * Número máximo de entradas (padrão: 1000)
   */
  maxEntries?: number;

  /**
   * Nome do cache para logging
   */
  name?: string;
}

/**
 * Estatísticas do cache
 */
export interface CacheStats {
  hits: number;
  misses: number;
  entries: number;
  maxEntries: number;
  hitRate: number;
}

// ============================================================================
// MEMORY CACHE
// ============================================================================

/**
 * Cache em memória com TTL e LRU eviction
 * 
 * @example
 * const cache = new MemoryCache<User>({ defaultTtl: 60000 });
 * 
 * cache.set('user:123', user);
 * const cached = cache.get('user:123');
 */
export class MemoryCache<T> {
  private cache: Map<string, CacheEntry<T>> = new Map();
  private readonly defaultTtl: number;
  private readonly maxEntries: number;
  private readonly name: string;
  
  // Estatísticas
  private hits = 0;
  private misses = 0;

  constructor(config: MemoryCacheConfig = {}) {
    this.defaultTtl = config.defaultTtl ?? 5 * 60 * 1000; // 5 minutos
    this.maxEntries = config.maxEntries ?? 1000;
    this.name = config.name ?? 'MemoryCache';
  }

  // ============================================================================
  // OPERAÇÕES BÁSICAS
  // ============================================================================

  /**
   * Obtém valor do cache
   * 
   * @returns Valor ou null se não existir/expirado
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      this.misses++;
      return null;
    }

    // Verifica expiração
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      this.misses++;
      return null;
    }

    this.hits++;
    
    // Move para o fim (LRU)
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.value;
  }

  /**
   * Define valor no cache
   * 
   * @param key Chave
   * @param value Valor
   * @param ttl TTL em ms (opcional, usa defaultTtl se não fornecido)
   */
  set(key: string, value: T, ttl?: number): void {
    // Eviction se necessário
    if (this.cache.size >= this.maxEntries) {
      this.evict();
    }

    const now = Date.now();
    const entry: CacheEntry<T> = {
      value,
      createdAt: now,
      expiresAt: now + (ttl ?? this.defaultTtl),
    };

    // Remove e adiciona para manter ordem LRU
    this.cache.delete(key);
    this.cache.set(key, entry);
  }

  /**
   * Remove valor do cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Verifica se chave existe (e não está expirada)
   */
  has(key: string): boolean {
    const value = this.get(key);
    return value !== null;
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
  }

  // ============================================================================
  // OPERAÇÕES AVANÇADAS
  // ============================================================================

  /**
   * Obtém valor ou executa função para obter e cachear
   * 
   * @example
   * const user = await cache.getOrSet('user:123', async () => {
   *   return await fetchUser(123);
   * });
   */
  async getOrSet(
    key: string,
    factory: () => T | Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cached = this.get(key);
    
    if (cached !== null) {
      return cached;
    }

    const value = await factory();
    this.set(key, value, ttl);
    return value;
  }

  /**
   * Obtém múltiplos valores de uma vez
   */
  getMany(keys: string[]): Map<string, T> {
    const results = new Map<string, T>();
    
    for (const key of keys) {
      const value = this.get(key);
      if (value !== null) {
        results.set(key, value);
      }
    }
    
    return results;
  }

  /**
   * Define múltiplos valores de uma vez
   */
  setMany(entries: Array<{ key: string; value: T; ttl?: number }>): void {
    for (const { key, value, ttl } of entries) {
      this.set(key, value, ttl);
    }
  }

  /**
   * Remove múltiplas chaves de uma vez
   */
  deleteMany(keys: string[]): number {
    let deleted = 0;
    
    for (const key of keys) {
      if (this.delete(key)) {
        deleted++;
      }
    }
    
    return deleted;
  }

  /**
   * Remove todas as chaves que começam com prefixo
   */
  deleteByPrefix(prefix: string): number {
    let deleted = 0;
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        deleted++;
      }
    }
    
    return deleted;
  }

  /**
   * Lista todas as chaves (não expiradas)
   */
  keys(): string[] {
    const now = Date.now();
    const validKeys: string[] = [];
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt > now) {
        validKeys.push(key);
      }
    }
    
    return validKeys;
  }

  /**
   * Retorna número de entradas válidas
   */
  get size(): number {
    this.cleanup(); // Remove expirados
    return this.cache.size;
  }

  // ============================================================================
  // ESTATÍSTICAS
  // ============================================================================

  /**
   * Retorna estatísticas do cache
   */
  getStats(): CacheStats {
    const total = this.hits + this.misses;
    
    return {
      hits: this.hits,
      misses: this.misses,
      entries: this.cache.size,
      maxEntries: this.maxEntries,
      hitRate: total > 0 ? this.hits / total : 0,
    };
  }

  /**
   * Reseta estatísticas
   */
  resetStats(): void {
    this.hits = 0;
    this.misses = 0;
  }

  // ============================================================================
  // MÉTODOS PRIVADOS
  // ============================================================================

  /**
   * Remove entrada mais antiga (LRU)
   */
  private evict(): void {
    // Map mantém ordem de inserção, primeiro é o mais antigo
    const oldestKey = this.cache.keys().next().value;
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Remove todas as entradas expiradas
   */
  private cleanup(): void {
    const now = Date.now();
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.expiresAt <= now) {
        this.cache.delete(key);
      }
    }
  }
}

// ============================================================================
// FACTORY
// ============================================================================

/**
 * Cria instância de MemoryCache
 */
export const createMemoryCache = <T>(config?: MemoryCacheConfig): MemoryCache<T> => {
  return new MemoryCache<T>(config);
};
